import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Snackbar,
  TextField,
  Alert
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { changePasswordAPI } from 'src/api';

import { STRINGS } from 'src/commons';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import { useDispatch, useSelector } from 'react-redux';

const SettingsPassword = () => {
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = loaderReducerAction;
  const { user } = useSelector((state) => state.common);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };

  return (
    <>
      <Card>
        <CardHeader title={STRINGS.CHANGE_PASSWORD} />
        <Divider />
        <CardContent>
          <Formik
            initialValues={{
              oldPassword: '',
              newPassword: '',
              confirm: ''
            }}
            validationSchema={Yup.object().shape({
              oldPassword: Yup.string()
                .max(20)
                .required(STRINGS.OLD_PASSWORD_REQUIRED),
              newPassword: Yup.string()
                .max(20)
                .required(STRINGS.NEW_PASSWORD_REQUIRED),
              confirm: Yup.string()
                .max(20)
                .required(STRINGS.CONFIRM_PASSWORD_REQUIRED)
                .oneOf(
                  [Yup.ref('newPassword'), null],
                  STRINGS.PASSWORD_MISMATCH
                )
            })}
            onSubmit={async (values) => {
              try {
                dispatch(startLoading());
                return changePasswordAPI(user, values)
                  .then((response) => {
                    const isError = response.status !== 200;
                    const message = isError ? response?.message : "Password Changed Successfully"
                    setSnackbarState({
                      open: true,
                      message: message,
                      severity: isError ? 'error' : 'success'
                    });
                  })
                  .catch((err) => {
                    console.log(err, 'ERROR');
                    throw err;
                  })
                  .finally(() => dispatch(stopLoading()));
              } catch (err) {
                setSnackbarState({
                  open: true,
                  message: err.message,
                  severity: 'error'
                });
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <TextField
                  error={Boolean(touched.oldPassword && errors.oldPassword)}
                  helperText={touched.oldPassword && errors.oldPassword}
                  fullWidth
                  label={STRINGS.OLD_PASSWORD}
                  margin="normal"
                  name="oldPassword"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="password"
                  value={values.oldPassword}
                  variant="outlined"
                />
                <TextField
                  error={Boolean(touched.newPassword && errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                  fullWidth
                  onBlur={handleBlur}
                  label={STRINGS.NEW_PASSWORD}
                  margin="normal"
                  name="newPassword"
                  onChange={handleChange}
                  type="password"
                  value={values.newPassword}
                  variant="outlined"
                />

                <TextField
                  error={Boolean(touched.confirm && errors.confirm)}
                  helperText={touched.confirm && errors.confirm}
                  fullWidth
                  label={STRINGS.CONFIRM_PASSWORD}
                  margin="normal"
                  name="confirm"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="password"
                  value={values.confirm}
                  variant="outlined"
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    textTransform: 'none',
                    p: 2
                  }}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {STRINGS.UPDATE}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </CardContent>
        <Divider />
      </Card>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarState.open}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <div>
          <Alert onClose={handleClose} severity={snackbarState.severity}>
            {snackbarState.message}
          </Alert>
        </div>
      </Snackbar>
    </>
  );
};

export default SettingsPassword;
